import { useCallback, useEffect, useState } from 'react'
import { Button, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import Krpano from 'utils/krpano'

import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import MemoFileIcon from 'components/icons/MemoFIleIcon'

import { MemoViewModalProps } from './type'

dayjs.extend(utc)
dayjs.extend(timezone)

function AnnotationMemoDetailModal({
  visible,
  memo,
  onClose
}: MemoViewModalProps) {
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [fetchedContent, setFetchedContent] = useState('')

  const handleFetchContent = useCallback(async () => {
    if (memo) {
      setIsLoadingContent(true)

      if (memo.s3_path) {
        try {
          const textContent = await Krpano.FetchMemoContent(memo.s3_path)

          const trimmedContent = textContent.trim()
          if (
            trimmedContent.startsWith('<') &&
            (trimmedContent.includes('</') || trimmedContent.includes('/>'))
          ) {
            setFetchedContent(textContent)
          } else {
            const escapedContent = textContent
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;')
            setFetchedContent(`<p>${escapedContent}</p>`)
          }
        } catch (error) {
          if (memo.s3_path) {
            setFetchedContent(memo.s3_path)
          } else {
            setFetchedContent(
              '<p class="text-gray-500">コンテンツを読み込めませんでした</p>'
            )
          }
        }
      }

      setIsLoadingContent(false)
    }
  }, [memo])

  useEffect(() => {
    if (visible && memo) {
      const timer = setTimeout(() => {
        handleFetchContent()
      }, 100)
      return () => clearTimeout(timer)
    }
    if (!visible) {
      setFetchedContent('')
    }
  }, [visible, memo, handleFetchContent])

  useEffect(() => {
    if (fetchedContent && !isLoadingContent) {
      const timer = setTimeout(() => {
        const container = document.querySelector('.ql-editor')
        if (container) {
          const images = container.querySelectorAll('img')
          images.forEach((img) => {
            img.style.maxWidth = '100%'
            img.style.height = 'auto'
            img.style.display = 'block'
            img.style.margin = '10px 0'
          })
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [fetchedContent, isLoadingContent])

  return (
    <Modal
      open={visible}
      width="840px"
      rootClassName="shared-permission-modal"
      onCancel={onClose}
    >
      <div className="flex flex-col w-auto min-h-[600px]">
        <div className="mb-[10px] flex items-center gap-2">
          <MemoFileIcon />
          <span className="text-xl font-bold">メモ詳細</span>
        </div>

        <div className="mb-6">
          <Typography className="text-lg font-bold text-gray-700 mb-2">
            メモ名
          </Typography>
          <Tooltip title={memo?.name || ''}>
            <Typography className="text-base text-gray-900 bg-gray-50 p-3 truncate w-full rounded border">
              {memo?.name || ''}
            </Typography>
          </Tooltip>
        </div>

        <div className="mb-6">
          <Typography className="text-lg font-bold text-gray-700 mb-2">
            メモ内容
          </Typography>
          <div className="border rounded bg-white min-h-[300px]">
            {isLoadingContent ? (
              <div className="flex items-center justify-center h-[300px]">
                <Spinner />
              </div>
            ) : (
              <div
                className="p-4 h-[300px] bg-gray-50 overflow-auto ql-editor"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: fetchedContent
                }}
              />
            )}
          </div>
        </div>

        {memo && (
          <div className="mb-6 text-xs text-gray-500 space-y-1">
            <div>
              作成日:{' '}
              {dayjs.utc(memo.created_at).tz().format('YYYY年M月D日 HH時mm分')}
            </div>
            <div>
              更新日:{' '}
              {dayjs.utc(memo.updated_at).tz().format('YYYY年M月D日 HH時mm分')}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-5 border-t border-gray-200">
          <Button className="w-[140px] h-[50px]" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AnnotationMemoDetailModal
